import React, { useEffect, useState } from "react";
import LazyLoad from "react-lazyload";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet"; // Import Helmet
import sanityClient from "../../sanityClient";

import before from "../../assets/images/construction/chr-before.webp";
import after from "../../assets/images/construction/chr-after.webp";
import CTASection from "../../components/CTASection";

const HomeRepairs = () => {
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "repairProgram"] | order(startDate desc) {
          _id,
          title,
          description,
          "imageUrl": image.asset->url
        }`
      )
      .then((data) => setPrograms(data))
      .catch(console.error);
  }, []);

  return (
    <>
      <Helmet>
        <title>Home Repairs - Habitat for Humanity Buffalo</title>
        <meta
          name="description"
          content="Learn about Habitat for Humanity Buffalo's Home Repair Programs and how you can get help with critical health and safety repairs for your home."
        />
      </Helmet>
      <Container>
        <h1>Home Repairs</h1>
        <SectionHeader className="section-header primary">
          Are you a homeowner in need of critical repairs?
        </SectionHeader>
        <p>
          Each year, Habitat for Humanity Buffalo partners with low-income
          homeowners to provide critical health and safety repairs to the
          interior and exterior of their homes. To determine if they qualify,
          homeowners must submit a complete program application along with
          documentation to establish their eligibility. Participant selection is
          based on funding, the need of the homeowner, and the condition of the
          home. Funding is limited and not all eligible applicants will be
          selected.
        </p>
        <FlexContainer>
          <FlexItem>
            <SectionHeader color="orange">Before</SectionHeader>
            <LazyLoad height={200} offset={100}>
              <img
                src={before}
                alt="Before home repairs"
                className="image"
                loading="lazy"
              />
            </LazyLoad>
          </FlexItem>
          <FlexItem>
            <SectionHeader color="green">After</SectionHeader>
            <LazyLoad height={200} offset={100}>
              <img
                src={after}
                alt="After home repairs"
                className="image"
                loading="lazy"
              />
            </LazyLoad>
          </FlexItem>
        </FlexContainer>
        <CTASection
          title="Apply for Home Repairs"
          description="Apply for our home repair program today and get the assistance you need to improve your living conditions."
          buttonText="Apply Now"
          buttonLink="https://habitatbflo.my.site.com/chrapp/s/"
        />
        <div className="help-section">
          <SectionHeader className="section-header green">
            Current Home Repair Programs
          </SectionHeader>
          <p>
            Habitat Buffalo can provide low-income homeowners with low-cost home
            repairs to address health, safety, and security issues in and around
            the home or to improve energy efficiency. Each program has different
            requirements and eligible repairs, which are subject to change.
            Funding is limited.
          </p>
          <p>
            Click below to view more information on program criteria,
            requirements and type of repairs offered. Applications are currently
            being accepted.
          </p>
          <CardContainer>
            {programs.map((program) => (
              <Card key={program._id}>
                <LazyLoad height={200} offset={100}>
                  <img
                    src={program.imageUrl}
                    alt={program.title}
                    className="image"
                    loading="lazy"
                  />
                </LazyLoad>
                <CardContent>
                  <CardTitle>{program.title}</CardTitle>
                  <CardDescription>{program.description}</CardDescription>
                  <CardButton to={`/how-to-apply/home-repairs/${program._id}`}>
                    Learn More
                  </CardButton>
                </CardContent>
              </Card>
            ))}
          </CardContainer>
        </div>
        <CTASection
          title="Apply for Home Repairs"
          description="Apply for our home repair program today and get the assistance you need to improve your living conditions."
          buttonText="Apply Now"
          buttonLink="https://habitatbflo.my.site.com/chrapp/s/"
        />
        <ContractorSection>
          <ContractorHeader>Become a Habitat Contractor</ContractorHeader>
          <ContractorParagraph>
            Habitat for Humanity Buffalo is always looking to partner with
            skilled contractors who can help us in our mission to provide safe
            and affordable housing. If you are a licensed and insured contractor
            interested in making a difference in the community, we invite you to
            learn more about our contractor program and how you can get
            involved.
          </ContractorParagraph>
          <ContractorButton to="/get-involved/become-a-habitat-buffalo-contractor">
            Learn More
          </ContractorButton>
        </ContractorSection>
      </Container>
    </>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 10px;
  }
`;

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FlexItem = styled.div`
  width: 48%;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background-color: var(--secondary-color);
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  text-align: center;
  position: relative;
`;

const CardContent = styled.div`
  text-align: left;
`;

const CardTitle = styled.h3`
  font-size: 1.5em;
  color: var(--primary-color);
  margin-bottom: 10px;

  @media (max-width: 480px) {
    font-size: 1.2em;
  }
`;

const CardDescription = styled.p`
  white-space: pre-line;
  margin-bottom: 20px;

  @media (max-width: 480px) {
    font-size: 0.9em;
  }
`;

const CardButton = styled(Link)`
  background-color: var(--hhb-primary);
  color: #fff;
  padding: 15px 30px;
  border: none;
  border-radius: 5px;
  font-size: 1.1em;
  font-weight: bold;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.3s, transform 0.3s;
  margin-top: 20px;
  display: inline-block;

  &:hover {
    background-color: var(--hhb-darkblue);
    transform: translateY(-2px);
  }

  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 1em;
  }
`;

const SectionHeader = styled.h2`
  color: var(--hhb-white);
  background-color: ${(props) => props.color || "var(--primary-color)"};
  padding: 10px;
  margin-bottom: 20px;
  text-align: center;
  border-radius: 5px;

  @media (max-width: 480px) {
    font-size: 1.2em;
    padding: 8px;
  }
`;

const ContractorSection = styled.div`
  margin-top: 40px;
  text-align: center;
  background-color: #f9f9f9;
  padding: 20px;
  border-radius: 8px;

  @media (max-width: 480px) {
    padding: 15px;
  }
`;

const ContractorHeader = styled.h2`
  font-size: 1.8em;
  margin-bottom: 20px;
  color: var(--hhb-primary);

  @media (max-width: 480px) {
    font-size: 1.5em;
    margin-bottom: 15px;
  }
`;

const ContractorParagraph = styled.p`
  font-size: 1.2em;
  margin-bottom: 20px;

  @media (max-width: 480px) {
    font-size: 1em;
    margin-bottom: 15px;
  }
`;

const ContractorButton = styled(Link)`
  background-color: var(--hhb-primary);
  color: #fff;
  padding: 15px 30px;
  border: none;
  border-radius: 5px;
  font-size: 1.1em;
  font-weight: bold;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.3s, transform 0.3s;

  &:hover {
    background-color: var(--hhb-darkblue);
    transform: translateY(-2px);
  }

  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 1em;
  }
`;

export default HomeRepairs;
